import React, { MutableRefObject, useRef } from "react";
import style from "../style.module.scss";
import qumuStyle from "./style.module.scss";
import classnames from "classnames";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { useScript } from "../../../../hooks/useScript";
import { mdiPlay } from "@mdi/js";
import { LiveStatus } from "../LiveStatus.enum.ts";
import { MediaStatus } from "../MediaStatus";
import isEqual from "lodash/isEqual";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { CookieConsent } from "@jmc/solid-design-system/src/utils/cookieConsent";

export interface Props {
    /** The Qumu video id */
    videoId: string;
    /** Current Playing Video ref*/
    playVideoRef?: MutableRefObject<any>;
    /** Should the player be playable? */
    videoPlayable?: boolean;
    /** Controls the offset of the video in seconds */
    offset?: number;
    /** Command to toggle playing mode */
    play?: boolean;
    /** A Background image that is shown when the video is not playing yet */
    thumbnail?: string;
    /** a callback triggered regularly while video is playing. See https://jnj.qumucloud.com/docs/player-api */
    onTimeUpdate?: () => void;
    /** What happens when the video starts playing */
    onPlay?: (videoref: MutableRefObject<any> | null) => void;
    /** SEO title */
    seoTitle?: string;
    /** Check time when paused and send to Parent element Cardimpression */
    mediastop?: (status: MediaStatus, currentTime: number | string) => void;
    /** Check time when the live state is updated */
    medialive?: (status: LiveStatus, liveStartTime: string, liveEndTime: string) => void;
    /** Should the duration be shown when the video is loaded? */
    showDuration?: boolean;
    /** Get total length time */
    getLength?: (duration: number) => void;
    /** Get cookie acceptance (Optanon) status */
    cookieAccept?: (accepted: boolean) => void;
    /** Get current time of player */
    getCurrentTime?: (currentTime: number) => void;
    className?: string;
}

declare global {
    interface Window {
        OptanonActiveGroups: any;
    }
}

export const Qumu: React.FunctionComponent<Props> = ({
    videoId,
    playVideoRef,
    videoPlayable = true,
    offset = 0,
    play = false,
    thumbnail,
    onTimeUpdate,
    onPlay,
    seoTitle = null,
    mediastop = null,
    medialive = null,
    getCurrentTime = null,
    getLength = null,
    cookieAccept = null,
    className = null,
}: Props) => {
    const [playing, setPlaying] = React.useState(play);
    const [showoverlay, setShowoverlay] = React.useState(true);
    const [playerApi, setPlayerApi] = React.useState(null);
    const videoref = useRef(null);

    const name = `qumuPlayer-${videoId}-${Math.random()}`;
    const initializeAPI = () => {
        const api = new (window as any).KV.Api(name);

        api.init((err: Error) => {
            if (err) {
                // eslint-disable-next-line no-console
                console.error("Error initializing Player API. Code: " + err);
            } else {
                // eslint-disable-next-line no-console
                console.info("Qumu API initialized for ", api.iframe(), api);
                setPlayerApi(api);

                api.bind("play", () => {
                    setPlaying(true);
                    setShowoverlay(false);
                    api.get("duration", function (videoduration: number) {
                        getLength && getLength(videoduration / 1000);
                    });
                    api.get("currentTime", function (currentTime: number) {
                        mediastop && mediastop(MediaStatus.PLAY, currentTime / 1000);
                    });
                    onPlay && onPlay(videoref);
                    api.get("liveState", (status: string) => {
                        // On demand videos always have a liveState of PRE_EVENT, so we only trigger the medialive hook if it's something else
                        if (status !== LiveStatus.PRE_EVENT) {
                            api.get("liveStartTime", function (startTime: string) {
                                api.get("liveEndTime", function (endTime: string) {
                                    medialive && medialive(status as LiveStatus, startTime, endTime);
                                });
                            });
                        }
                    });
                });

                api.bind("pause", () => {
                    setPlaying(false);
                    api.get("currentTime", function (currentTime: number) {
                        mediastop && mediastop(MediaStatus.PAUSE, currentTime / 1000);
                    });
                });
                api.bind("timeupdate", () => {
                    onTimeUpdate && onTimeUpdate();
                    api.get("currentTime", function (currentTime: number) {
                        getCurrentTime && getCurrentTime(currentTime / 1000);
                    });
                });
                api.bind("liveState", (status: string) => {
                    api.get("liveStartTime", function (startTime: string) {
                        api.get("liveEndTime", function (endTime: string) {
                            medialive && medialive(status as LiveStatus, startTime, endTime);
                        });
                    });
                });
            }
        });
    };

    const scriptStatus = useScript(
        `https://jnj.qumucloud.com/client/application/api-v2.js`,
        undefined,
        CookieConsent.STRICTLY_NECESSARY,
    );

    React.useEffect(() => {
        if (scriptStatus === "ready" && !playerApi) {
            initializeAPI();
        }
        if (window?.OptanonActiveGroups?.split(",")?.includes(`${CookieConsent.STRICTLY_NECESSARY}`)) {
            cookieAccept && cookieAccept(true);
        }
    }, [scriptStatus]);

    React.useEffect(() => {
        if (!playerApi) return;
        playerApi.set("currentTime", offset * 1000);
        /* get length of video and set media impression */
        playerApi.get("duration", function (videoduration: number) {
            getLength && getLength(videoduration / 1000);
        });
    }, [playerApi, offset]);

    React.useEffect(() => {
        playerApi && playerApi.command("play");
    }, [offset]);

    // Pause all other player based on video reference
    React.useEffect(() => {
        if (
            playerApi &&
            playVideoRef &&
            playVideoRef.current &&
            !isEqual(playVideoRef.current, videoref.current) &&
            playing
        ) {
            playerApi.command("pause");
        }
    }, [playVideoRef, playerApi]);

    return (
        <div
            className={classnames([className, videoPlayable ? style.player : style.disabled, qumuStyle.player])}
            onMouseOver={() => !thumbnail && setShowoverlay(false)}
            onFocus={() => !thumbnail && setShowoverlay(false)}
            onMouseOut={() => !thumbnail && !playing && setShowoverlay(true)}
            onBlur={() => !thumbnail && !playing && setShowoverlay(true)}
            ref={videoref}
        >
            <iframe
                name={name}
                data-test-id={`VideoPlayer.${videoId}`}
                className={qumuStyle.player__iframe}
                frameBorder="0"
                allow="fullscreen"
                src={`https://jnj.qumucloud.com/view/${videoId}?autoplay=${play}&t=mo&inline=true&volume=100`}
                title={seoTitle}
            />
            {showoverlay && !playing && (
                <div
                    className={classnames([style.player__content, qumuStyle.player__content])}
                    style={thumbnail && { backgroundImage: `url(${thumbnail})` }}
                >
                    {thumbnail && (
                        <div
                            className={qumuStyle.player__button}
                            data-test-id={`VideoPlayer.PlayButton`}
                            onClick={() => {
                                playerApi && playerApi.command("play");
                                setShowoverlay(false);
                            }}
                            onKeyDown={(e: KeyboardEvent): void =>
                                keyHandler(e.key, () => {
                                    playerApi && playerApi.command("play");
                                    setShowoverlay(false);
                                })
                            }
                            role="button"
                            tabIndex={0}
                        >
                            <Icon icon={mdiPlay} size="large" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Qumu;
